import React, { useCallback, useEffect, useRef } from "react";

import { Box, Flex } from "@chakra-ui/react";
import Cropper, { Area, Point } from "react-easy-crop";

import { useElementWidthLayout } from "hooks/useElementWidthLayout";

import CropHelperText from "./CropHelperText";
import RatioSelector from "./RatioSelector";
import ZoomSlider from "./ZoomSlider";

export interface ICropRatioOption {
  ratio: number;
  label: string;
  example?: { width: number; height: number };
}

export const CROP_INSET = 24;
export const MIN_ZOOM = 0.7;
export const MAX_ZOOM = 5;
export const ZOOM_STEP = 0.1;

export interface ICropPanelProps {
  image: string;
  cropperAspect: number;
  setCropperAspect: (aspect: number) => void;
  crop: Point;
  setCrop: (point: Point) => void;
  zoom: number;
  setZoom: (zoom: number) => void;
  onCropAreaChange: (croppedAreaPixels: Area) => void;
  ratioOptions?: ICropRatioOption[];
  // When true, render the "Move or pinch…" helper text at the bottom of this
  // panel (inside the scrollable area). When false, the parent renders it in
  // the pinned footer. Used for short viewports where the footer can't host
  // the helper without overlapping panel content.
  showHelperText?: boolean;
}

const CropPanel: React.FC<ICropPanelProps> = ({
  image,
  cropperAspect,
  setCropperAspect,
  crop,
  setCrop,
  zoom,
  setZoom,
  onCropAreaChange,
  ratioOptions,
  showHelperText = false,
}) => {
  const { ref: containerRef, width: containerWidth } = useElementWidthLayout();
  const hasAutoZoomedRef = useRef(false);

  useEffect(() => {
    hasAutoZoomedRef.current = false;
  }, [image]);

  const cropWidth = Math.max(0, containerWidth - 2 * CROP_INSET);
  const cropHeight = cropWidth / cropperAspect;
  // Pin container to the 1:1 size so switching aspect ratios doesn't resize the modal.
  const containerHeight = cropWidth + 2 * CROP_INSET;
  const hasMeasured = cropWidth > 0;

  const handleCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      onCropAreaChange(croppedAreaPixels);
    },
    [onCropAreaChange],
  );

  const handleMediaLoaded = useCallback(
    (mediaSize: {
      naturalWidth: number;
      naturalHeight: number;
      width: number;
      height: number;
    }) => {
      if (!mediaSize.width || !mediaSize.height) return;
      // Only auto-zoom once per image. The chain is non-obvious: when the Crop
      // panel is hidden (display:none on tab switch), the container's
      // clientWidth drops to 0, the ResizeObserver inside useElementWidthLayout
      // fires with width=0, `hasMeasured` flips false, and the JSX gate
      // unmounts the Cropper. On return, the Cropper remounts and
      // onMediaLoaded fires again — without this guard it would clobber the
      // user's manual zoom every time they come back to the Crop tab.
      if (hasAutoZoomedRef.current) return;
      hasAutoZoomedRef.current = true;
      const requiredZoom = Math.max(
        cropWidth / mediaSize.width,
        cropHeight / mediaSize.height,
      );
      if (requiredZoom <= 1) return;
      setZoom(Math.min(requiredZoom, MAX_ZOOM));
    },
    [cropWidth, cropHeight, setZoom],
  );

  return (
    <Flex direction="column" w="100%" gap="16px" px="16px">
      <Box
        ref={containerRef}
        position="relative"
        w="100%"
        overflow="hidden"
        h={hasMeasured ? `${containerHeight}px` : "320px"}
      >
        {hasMeasured && (
          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            cropSize={{ width: cropWidth, height: cropHeight }}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={handleCropComplete}
            onMediaLoaded={handleMediaLoaded}
            restrictPosition={false}
            objectFit="auto-cover"
            showGrid
          />
        )}
      </Box>

      <ZoomSlider
        minZoom={MIN_ZOOM}
        maxZoom={MAX_ZOOM}
        step={ZOOM_STEP}
        zoom={zoom}
        setZoom={setZoom}
      />

      {Array.isArray(ratioOptions) && ratioOptions.length > 1 && (
        <RatioSelector
          options={ratioOptions}
          activeRatio={cropperAspect}
          onSelect={setCropperAspect}
        />
      )}

      {showHelperText && <CropHelperText />}
    </Flex>
  );
};

export default CropPanel;
